import { Check, Clear } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  AlertSeverity,
  PowerBiReport,
  setAlert,
  useAppDispatch,
  useAppSelector,
  useGetAllPowerBiReportsQuery,
  useGetUserPowerBiReportsQuery,
  usePostUserPowerBiForcedClaimsMutation,
} from 'api';
import { TabPanelContainer } from 'app/common';
import { useEffect, useState } from 'react';

export const ReportsTabPanel: React.FC<{ value: number; index: number }> = ({
  value,
  index,
}) => {
  const dispatch = useAppDispatch();
  const { selectedUser } = useAppSelector(state => state.userMaintenance);
  const { data: powerBiReports } = useGetAllPowerBiReportsQuery();
  const { data: userPowerBiReports, refetch } = useGetUserPowerBiReportsQuery(
    selectedUser?.id
  );
  const [updateAction, { isLoading: isUpdating, isSuccess, isError }] =
    usePostUserPowerBiForcedClaimsMutation();

  const [userPowerBiReportsState, setUserPowerBiReportsState] =
    useState<PowerBiReport[]>();

  useEffect(() => {
    if (userPowerBiReports && powerBiReports && powerBiReports.length > 0) {
      const updateUserPowerBiReports = powerBiReports
        .map(pbi => {
          const foundReport = userPowerBiReports.find(
            upbi => pbi.reportID === upbi.reportID
          );
          if (foundReport) return { ...foundReport, active: true };
          return { ...pbi, active: false };
        })
        .sort((a, b) => a.reportName.localeCompare(b.reportName));

      setUserPowerBiReportsState(updateUserPowerBiReports);
    }
    // eslint-disable-next-line
  }, [powerBiReports, userPowerBiReports]);

  function handleCheckboxChange(
    e: React.ChangeEvent<HTMLInputElement>,
    powerBiReport: PowerBiReport
  ) {
    const updatedUserPowerBiReports = userPowerBiReportsState?.map(upbi =>
      upbi.reportID === powerBiReport.reportID
        ? { ...upbi, active: !upbi.active }
        : { ...upbi }
    );

    setUserPowerBiReportsState(updatedUserPowerBiReports);
  }

  const handleSave = async () => {
    const reports = userPowerBiReportsState?.filter(rep => rep.active);

    if (reports && selectedUser) {
      await updateAction({
        userId: selectedUser.id,
        powerBiReports: reports,
      })
        .unwrap()
        .then(() => {
          dispatch(
            setAlert({
              message: 'Roles saved.',
              severity: AlertSeverity.Success,
            })
          );
          refetch();
        })
        .catch(() => {
          dispatch(
            setAlert({
              message: 'Something went wrong.',
              severity: AlertSeverity.Error,
            })
          );
        });
    }
  };

  const getStartIcon = () => {
    if (isUpdating)
      return <CircularProgress size={20} sx={{ color: grey[50] }} />;
    else if (isSuccess) return <Check sx={{ color: grey[50] }} />;
    else if (isError) return <Clear sx={{ color: grey[50] }} />;
  };

  return (
    <TabPanelContainer value={value} index={index}>
      <FormGroup
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)', // Fixed 5 columns
          gap: '16px', // Spacing between items
          overflowX: 'auto', // Enable horizontal scrolling if needed
          width: '100%', // Ensure the grid takes up the full width
        }}
      >
        {userPowerBiReportsState &&
          selectedUser &&
          userPowerBiReportsState.map((pbiReport, index) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={pbiReport.active}
                  onChange={e => handleCheckboxChange(e, pbiReport)}
                />
              }
              label={pbiReport.reportName}
              key={`${pbiReport.reportID}-${index}`}
            />
          ))}
      </FormGroup>

      <Box display="flex" justifyContent="center" paddingTop="5px">
        {userPowerBiReportsState && selectedUser && (
          <Button
            variant="contained"
            size="large"
            sx={{ width: '300px', display: 'flex', alignItems: 'center' }}
            onClick={handleSave}
            startIcon={getStartIcon()}
          >
            <Typography>Save</Typography>
          </Button>
        )}
      </Box>
    </TabPanelContainer>
  );
};
